import { useState, useCallback, useMemo } from "react";
import pluralize from "pluralize";

import { ClickableElement } from "@rapid/components/ClickableElement";
import { CreateWorkflowFromRapidModal } from "@rapid/components/flow/shared/Deploy/CreateWorkflowFromRapidModal";
import { ThresholdTracker } from "@rapid/components/flow/shared/Deploy/ThresholdTracker";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@rapid/analytics/types";
import { useFlow } from "@rapid/contexts/FlowContext";
import { useAuth } from "@rapid/contexts/AuthContext";
import { useSignInAndCreateWorkflow } from "@rapid/hooks/useSignInAndCreateWorkflow";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { useReviewBatchCount } from "@rapid/hooks/useReviewBatchCount";
import { useDatasetImageCount } from "@rapid/hooks/useDatasetImageCount";
import { useFlushCITrainingJob } from "@rapid/hooks/useFlushCITrainingJob";
import { useImproveModel } from "@rapid/hooks/useImproveModel";
import { getImproveButtonProps } from "@rapid/hooks/improveButtonProps";
import {
    usePredictionsForFileAndFrameSegment,
    useIsTraining,
    useCurrentRapidData
} from "@rapid/store";
import { useCurrentWorkflow } from "@rapid/hooks/useCurrentWorkflow";
import { CIEvaluationResult } from "@functions/continuousImprovement/continuousImprovementTypes";
import { useCurrentWorkspace } from "@rapid/hooks/useCurrentWorkspace";
import { useIsInlineEmbeddedRapid } from "@rapid/providers/EmbeddedContext";
import { setCachedInferenceResults } from "@rapid/utils/api/datasets";
import { getNextThreshold } from "@rapid/utils/getNextThreshold";
import { DeploySteps, Flows } from "@rapid/types/flows";
import { RapidDeployModal } from "@rapid/components/flow/shared/Deploy/RapidDeployModal";
import { TooltipContainer } from "@scripts/react/components/Tooltip";

export const IntegrateSideMenu = () => {
    const { task } = useFlow();
    const { track } = useAnalytics();
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const { user, requireLogin } = useAuth();
    const { setStep } = useFlowState();
    const { signInAndCreateWorkflow, loading: signInLoading } = useSignInAndCreateWorkflow();
    const { currentWorkflow: workflow } = useCurrentWorkflow();
    const { currentWorkspace: workspace } = useCurrentWorkspace();
    const predictions = usePredictionsForFileAndFrameSegment();
    const { count: reviewBatchCount, newDataReady } = useReviewBatchCount();
    const { loading: improveLoading } = useImproveModel();
    const isTraining = useIsTraining();
    const currentRapidData = useCurrentRapidData();
    const { count: datasetImageCount, datasetId } = useDatasetImageCount();
    const isInlineEmbeddedRapid = useIsInlineEmbeddedRapid();

    const hasScheduledJob =
        currentRapidData?.lastCIEvaluation?.result === CIEvaluationResult.TRAINING_SCHEDULED;
    const { flush: handleTrainNowClick, isFlushing } = useFlushCITrainingJob(datasetId, {
        force: hasScheduledJob
    });

    const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
    const highlightImproveButton = !isTraining && (reviewBatchCount ?? 0) >= 10;

    const imagesUntilRetrain =
        datasetImageCount !== null ? getNextThreshold(datasetImageCount) - datasetImageCount : null;

    const tooltipMessage = useMemo(() => {
        if (imagesUntilRetrain === null) return "";
        if (isTraining)
            return "Your model is already retraining. New data will be used in the next cycle.";
        if (newDataReady) {
            return "New data is ready to improve your model.";
        }
        return `Upload, review, and approve ${imagesUntilRetrain} more ${pluralize("image", imagesUntilRetrain)} to trigger the next retrain.`;
    }, [isTraining, newDataReady, imagesUntilRetrain]);

    const handleIntegrateClick = useCallback(() => {
        setIsDeployModalOpen(true);
        track({ event: AnalyticsFlowEvent.DATA_MODE_SELECTED, data: { task, dataMode: "hosted" } });
    }, [track, task]);

    const handleImproveClick = useCallback(() => {
        if (workflow && workspace) {
            if (workflow.url && Object.keys(predictions).length > 0) {
                setCachedInferenceResults(workflow.url, { predictions, annotations: {} });
            }

            setStep({
                flow: Flows.DEPLOY,
                step: DeploySteps.IMPROVE,
                workspaceUrl: workspace.url,
                workflowUrl: workflow.url
            });
        } else {
            signInAndCreateWorkflow();
        }
    }, [workflow, workspace, predictions, setStep, signInAndCreateWorkflow]);

    const isImproveDisabled = improveLoading || (signInLoading && !workflow);
    const baseImproveProps = useMemo(
        () =>
            getImproveButtonProps(isTraining, improveLoading, {
                willPublish: false,
                iconStyle: "fad"
            }),
        [isTraining, improveLoading]
    );

    // When a training job is scheduled and the model isn't already training,
    // replace the entire Improve card with a "Train Now" CTA.
    const showTrainNowCard = hasScheduledJob && !isTraining;
    const improveProps = showTrainNowCard
        ? {
              title: "Train Now",
              description: "New data is ready. Start retraining your model.",
              icon: "fad fa-rotate"
          }
        : baseImproveProps;

    const handleDeployModalClose = useCallback(() => setIsDeployModalOpen(false), []);

    return (
        <div className="content flex flex-col justify-between">
            <div className="flex flex-col gap-4">
                <ClickableElement
                    icon="fad fa-code"
                    title="Deploy Model"
                    description="Use your model on video or images."
                    onClick={handleIntegrateClick}
                    isSelected={false}
                />
                <TooltipContainer
                    type="titleOnly"
                    title={tooltipMessage}
                    placement="right"
                    disabled={!tooltipMessage || showTrainNowCard}
                    customButton={
                        <ClickableElement
                            icon={improveProps.icon}
                            title={improveProps.title}
                            description={improveProps.description}
                            onClick={showTrainNowCard ? handleTrainNowClick : handleImproveClick}
                            isSelected={showTrainNowCard || newDataReady}
                            className={
                                showTrainNowCard || newDataReady
                                    ? "bg-white hover:bg-tint"
                                    : undefined
                            }
                            chip={showTrainNowCard ? "" : newDataReady ? "New Data Ready" : ""}
                            disabled={showTrainNowCard ? isFlushing : isImproveDisabled}
                        >
                            {datasetImageCount !== null && !isTraining && !showTrainNowCard && (
                                <ThresholdTracker
                                    currentImages={datasetImageCount}
                                    highlight={newDataReady}
                                />
                            )}
                        </ClickableElement>
                    }
                />
                {!isInlineEmbeddedRapid && (
                    <>
                        <ClickableElement
                            icon="fad fa-network-wired"
                            title="Add Intelligence & Automation"
                            description="Add complex logic to your model with workflows."
                            onClick={() => {
                                track({
                                    event: AnalyticsFlowEvent.OPEN_WORKFLOWS_BUTTON_CLICKED,
                                    data: {}
                                });
                                if (!user) {
                                    requireLogin(() => setIsTemplateModalOpen(true));
                                    return;
                                }
                                setIsTemplateModalOpen(true);
                            }}
                            isSelected={false}
                        />

                        <CreateWorkflowFromRapidModal
                            isOpen={isTemplateModalOpen}
                            onClose={() => setIsTemplateModalOpen(false)}
                        />
                    </>
                )}
            </div>

            <RapidDeployModal isOpen={isDeployModalOpen} onClose={handleDeployModalClose} />
        </div>
    );
};
